import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { NAV_CARDS, COLOR_MAP } from "@/constants/nav";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ExternalLink, ArrowUpRight, Bell, Clock, CheckCircle2, Table2 } from "lucide-react";
import { cn } from "@/lib/utils";

export default function Dashboard() {
  const { user, isAdmin } = useAuth();
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState([]);
  const [notices, setNotices] = useState([]);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    api.get("/content/modules").then((r) => setModules(r.data)).catch(() => {});
    api.get("/progress").then((r) => setProgress(r.data)).catch(() => {});
    api.get("/content/notices").then((r) => setNotices(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const passed = progress.filter((p) => p.passed).length;
  const pct = modules.length ? Math.round((passed / modules.length) * 100) : 0;

  const recent = [...modules]
    .sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""))
    .slice(0, 4);

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Hero */}
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-mlily-blue">Welcome back, {user?.name?.split(" ")[0]}</div>
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 mt-2">
          MLILY Warranty Training Centre
        </h1>
        <p className="text-slate-500 mt-2 max-w-2xl">
          Train, troubleshoot and follow approved procedures. Every warranty case begins with the online claim form.
        </p>
      </div>

      {/* Warranty tracker button */}
      <a
        href={settings.warranty_tracker_url || "#"}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="open-warranty-tracker-btn"
        className="group flex items-center justify-between gap-4 rounded-2xl bg-mlily-blue px-6 py-5 text-white shadow-lg shadow-blue-900/20 hover:bg-mlily-bluehover transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center">
            <Table2 className="w-6 h-6" />
          </div>
          <div>
            <div className="font-heading text-lg font-bold">OPEN LIVE WARRANTY TRACKER</div>
            <div className="text-blue-100 text-sm">Live cases are tracked in the WorkDrive spreadsheet (opens in new tab)</div>
          </div>
        </div>
        <ExternalLink className="w-5 h-5 opacity-80 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
      </a>

      {/* Nav cards bento */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
        {NAV_CARDS.map((c, i) => {
          const cm = COLOR_MAP[c.color];
          const Icon = c.icon;
          return (
            <motion.div
              key={c.key}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05, duration: 0.4 }}
              className={cn(c.span ? "md:col-span-2" : "md:col-span-1")}
            >
              <Link to={c.to} data-testid={`card-${c.key}`}>
                <Card className={cn(
                  "group relative h-full min-h-[150px] overflow-hidden border rounded-xl p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg bg-white",
                  cm.border
                )}>
                  {c.img && (
                    <div className="absolute inset-0 opacity-[0.07] group-hover:opacity-[0.12] transition-opacity">
                      <img src={c.img} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="relative flex flex-col h-full">
                    <div className={cn("w-11 h-11 rounded-xl flex items-center justify-center mb-3", cm.soft)}>
                      <Icon className={cn("w-6 h-6", cm.text)} />
                    </div>
                    <h3 className="font-heading font-bold text-slate-900 text-lg">{c.title}</h3>
                    <p className="text-sm text-slate-500 mt-1">{c.desc}</p>
                    <ArrowUpRight className={cn("w-4 h-4 mt-auto ml-auto opacity-0 group-hover:opacity-100 transition-opacity", cm.text)} />
                  </div>
                </Card>
              </Link>
            </motion.div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Progress */}
        <Card className="p-6" data-testid="training-progress-card">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            <h3 className="font-heading font-bold text-slate-900">Your Training Progress</h3>
          </div>
          <div className="flex items-end justify-between mb-2">
            <span className="text-3xl font-heading font-extrabold text-slate-900">{pct}%</span>
            <span className="text-sm text-slate-500">{passed}/{modules.length} modules</span>
          </div>
          <Progress value={pct} className="h-2" />
          <div className="mt-4 space-y-2 max-h-48 overflow-y-auto">
            {modules.map((m) => {
              const p = progress.find((x) => x.module_id === m.id);
              const state = p?.passed ? "done" : p ? "progress" : "todo";
              return (
                <div key={m.id} className="flex items-center gap-2 text-sm">
                  <span>{state === "done" ? "✅" : state === "progress" ? "⏳" : "⬜"}</span>
                  <span className={cn("truncate", state === "done" ? "text-slate-900" : "text-slate-500")}>{m.title}</span>
                </div>
              );
            })}
          </div>
          <Link to="/training" className="text-sm font-medium text-mlily-blue mt-4 inline-block" data-testid="progress-continue-link">
            Continue training →
          </Link>
        </Card>

        {/* Notices */}
        <Card className="p-6" data-testid="notices-card">
          <div className="flex items-center gap-2 mb-4">
            <Bell className="w-5 h-5 text-amber-500" />
            <h3 className="font-heading font-bold text-slate-900">Important Warranty Notices</h3>
          </div>
          <div className="space-y-3">
            {notices.length === 0 && <p className="text-sm text-slate-400">No notices.</p>}
            {notices.map((n) => (
              <div key={n.id} className="rounded-lg border border-amber-100 bg-amber-50/60 p-3">
                <div className="text-sm font-semibold text-slate-900">{n.title}</div>
                <div className="text-xs text-slate-600 mt-0.5">{n.body}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* Recently updated */}
        <Card className="p-6" data-testid="recent-updates-card">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-mlily-blue" />
            <h3 className="font-heading font-bold text-slate-900">Recently Updated Procedures</h3>
          </div>
          <div className="space-y-3">
            {recent.map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-2">
                <span className="text-sm text-slate-700 truncate">{m.title}</span>
                <span className="text-xs text-slate-400 shrink-0">{(m.updated_at || "").slice(0, 10)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {isAdmin && (
        <Link to="/admin" data-testid="dashboard-admin-link"
          className="block rounded-xl border border-dashed border-slate-300 p-4 text-center text-sm text-slate-600 hover:border-mlily-blue hover:text-mlily-blue transition-colors">
          You are an Admin — open the Admin Area to manage content, users and settings →
        </Link>
      )}
    </div>
  );
}
